import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { ArrowLeft, Loader2, FileEdit, Plus, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { API } from "@/lib/newsUtils";

const emptyForm = { title: "", meta_title: "", meta_description: "", html: "", sections: [] };

export default function AdminBackoffice() {
  const navigate = useNavigate();
  const [pages, setPages] = useState([]);
  const [selectedSlug, setSelectedSlug] = useState("");
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);

  const load = (p) =>
    setForm({
      title: p.title || "",
      meta_title: p.meta_title || "",
      meta_description: p.meta_description || "",
      html: p.html || "",
      sections: Array.isArray(p.sections) ? p.sections.map((s) => ({ q: s.q || "", a: s.a || "" })) : [],
    });

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API}/admin/pages`);
        setPages(data);
        if (data.length) {
          setSelectedSlug(data[0].slug);
          load(data[0]);
        }
      } catch { /* silencioso */ }
    })();
  }, []);

  const onSelectPage = (slug) => {
    setSelectedSlug(slug);
    const p = pages.find((x) => x.slug === slug);
    if (p) load(p);
  };

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setSection = (i, k, v) =>
    setForm((f) => ({ ...f, sections: f.sections.map((s, idx) => (idx === i ? { ...s, [k]: v } : s)) }));
  const addSection = () => setForm((f) => ({ ...f, sections: [...f.sections, { q: "", a: "" }] }));
  const removeSection = (i) => setForm((f) => ({ ...f, sections: f.sections.filter((_, idx) => idx !== i) }));

  const isHome = selectedSlug === "home";

  const save = async () => {
    if (!selectedSlug) return;
    setSaving(true);
    try {
      const { data } = await axios.put(`${API}/admin/pages/${selectedSlug}`, form);
      setPages((prev) => prev.map((p) => (p.slug === selectedSlug ? data : p)));
      load(data);
      toast.success("Página guardada.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "No se pudo guardar la página");
    } finally {
      setSaving(false);
    }
  };

  const previewHref = isHome ? "/" : `/${selectedSlug}`;

  return (
    <div className="min-h-screen bg-background" data-testid="admin-backoffice">
      <header className="sticky top-0 z-50 bg-background/90 backdrop-blur-xl border-b border-border">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 bg-primary flex items-center justify-center">
              <FileEdit className="w-5 h-5 text-white" />
            </div>
            <h1 className="font-heading text-lg font-black tracking-tight">BACKOFFICE</h1>
          </div>
          <Button
            data-testid="admin-back-button"
            variant="outline"
            onClick={() => navigate("/admin")}
            className="rounded-none border-border h-10 w-10 px-0 sm:w-auto sm:px-4 gap-2"
          >
            <ArrowLeft className="w-4 h-4" />
            <span className="hidden sm:inline">Volver</span>
          </Button>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 sm:px-6 py-8">
        {pages.length === 0 ? (
          <p className="text-muted-foreground">Cargando páginas...</p>
        ) : (
          <section data-testid="admin-pages-editor" className="border border-border p-5 space-y-5">
            <div>
              <h2 className="font-heading text-lg font-black tracking-tight mb-1">SEO y contenido de páginas</h2>
              <p className="text-sm text-muted-foreground">
                Edita el título y la descripción para buscadores, y añade secciones tipo acordeón/FAQ que
                aparecerán sobre el pie de página.
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-[220px_1fr] gap-4">
              <div>
                <Label className="text-xs uppercase tracking-wide font-semibold">Página</Label>
                <Select value={selectedSlug} onValueChange={onSelectPage}>
                  <SelectTrigger data-testid="page-select" className="rounded-none border-border mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="rounded-none">
                    {pages.map((p) => (
                      <SelectItem key={p.slug} value={p.slug} data-testid={`page-option-${p.slug}`}>
                        {p.slug === "home" ? "Inicio (Home)" : p.title}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="page-title" className="text-xs uppercase tracking-wide font-semibold">Título interno</Label>
                <Input
                  id="page-title"
                  data-testid="page-title-input"
                  className="rounded-none border-border mt-1"
                  value={form.title}
                  onChange={(e) => setField("title", e.target.value)}
                />
              </div>
            </div>

            <div>
              <Label htmlFor="meta-title" className="text-xs uppercase tracking-wide font-semibold">
                Meta título (SEO)
              </Label>
              <Input
                id="meta-title"
                data-testid="meta-title-input"
                className="rounded-none border-border mt-1"
                value={form.meta_title}
                maxLength={70}
                onChange={(e) => setField("meta_title", e.target.value)}
                placeholder="Título que aparece en Google (≤ 60-70 caracteres)"
              />
              <p className="text-[11px] text-muted-foreground mt-1">{form.meta_title.length}/70</p>
            </div>

            <div>
              <Label htmlFor="meta-desc" className="text-xs uppercase tracking-wide font-semibold">
                Meta descripción (SEO)
              </Label>
              <Textarea
                id="meta-desc"
                data-testid="meta-description-input"
                className="rounded-none border-border mt-1 min-h-[70px]"
                value={form.meta_description}
                maxLength={180}
                onChange={(e) => setField("meta_description", e.target.value)}
                placeholder="Descripción que aparece bajo el título en Google (≤ 155-160 caracteres)"
              />
              <p className="text-[11px] text-muted-foreground mt-1">{form.meta_description.length}/180</p>
            </div>

            {!isHome && (
              <div>
                <Label htmlFor="page-html" className="text-xs uppercase tracking-wide font-semibold">
                  Contenido principal (HTML)
                </Label>
                <Textarea
                  id="page-html"
                  data-testid="page-html-input"
                  className="rounded-none border-border mt-1 font-mono text-xs min-h-[180px]"
                  value={form.html}
                  onChange={(e) => setField("html", e.target.value)}
                  placeholder="<p>Escribe aquí el contenido en HTML...</p>"
                />
                <p className="text-[11px] text-muted-foreground mt-1">
                  Etiquetas permitidas: &lt;h2&gt;, &lt;p&gt;, &lt;ul&gt;&lt;li&gt;, &lt;a href&gt;, &lt;strong&gt;, etc.
                </p>
              </div>
            )}

            {/* Accordion / FAQ sections */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <Label className="text-xs uppercase tracking-wide font-semibold">
                  Secciones (acordeón / FAQ) — se muestran sobre el footer
                </Label>
                <Button
                  type="button"
                  data-testid="add-section-button"
                  onClick={addSection}
                  variant="outline"
                  className="rounded-none border-border h-8 text-xs gap-1.5"
                >
                  <Plus className="w-3.5 h-3.5" /> Añadir sección
                </Button>
              </div>

              {form.sections.length === 0 ? (
                <p className="text-sm text-muted-foreground border border-dashed border-border p-4">
                  No hay secciones. Añade la primera para crear un bloque plegable de contenido.
                </p>
              ) : (
                <div className="space-y-3" data-testid="sections-editor">
                  {form.sections.map((s, i) => (
                    <div key={i} className="border border-border p-4 space-y-2" data-testid={`section-row-${i}`}>
                      <div className="flex items-center gap-2">
                        <Input
                          data-testid={`section-q-${i}`}
                          className="rounded-none border-border font-semibold"
                          value={s.q}
                          onChange={(e) => setSection(i, "q", e.target.value)}
                          placeholder="Pregunta / título de la sección"
                        />
                        <Button
                          type="button"
                          data-testid={`section-remove-${i}`}
                          onClick={() => removeSection(i)}
                          variant="outline"
                          className="rounded-none border-border h-9 w-9 px-0 text-muted-foreground hover:text-red-600 hover:border-red-300 shrink-0"
                          aria-label="Eliminar sección"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                      <Textarea
                        data-testid={`section-a-${i}`}
                        className="rounded-none border-border min-h-[90px]"
                        value={s.a}
                        onChange={(e) => setSection(i, "a", e.target.value)}
                        placeholder="Respuesta / contenido de la sección"
                      />
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="flex items-center gap-3 pt-2">
              <Button
                data-testid="save-page-button"
                onClick={save}
                disabled={saving}
                className="rounded-none bg-primary hover:bg-blue-900 h-10 gap-2"
              >
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
                Guardar página
              </Button>
              {selectedSlug && (
                <a
                  href={previewHref}
                  target="_blank"
                  rel="noreferrer"
                  data-testid="page-preview-link"
                  className="text-sm text-primary hover:underline"
                >
                  Ver página →
                </a>
              )}
            </div>
          </section>
        )}
      </main>
    </div>
  );
}
